import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Crown } from "lucide-react";
import { api, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";

const PERIODS = ["week", "month", "all"];
const GAMES = { dice: "/leaderboard", ludo: "/ludo/leaderboard", dames: "/dames/leaderboard" };

export default function LeaderboardPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [period, setPeriod] = useState("week");
  const [game, setGame] = useState("dice");
  const { data } = useQuery({ queryKey: ["leaderboard", game, period], queryFn: async () => (await api.get(GAMES[game], { params: { period } })).data });
  return (
    <div className="mx-auto max-w-2xl space-y-5 rise" data-testid="leaderboard-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("board.title")}</h1>
      <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
        {Object.keys(GAMES).map((g) => (
          <button key={g} onClick={() => setGame(g)} data-testid={`board-game-${g}`}
            className={`h-10 rounded-full px-4 text-sm sm:px-6 font-semibold transition-colors ${game === g ? "bg-amber-500 text-[#0B0D12]" : "text-slate-400"}`}>{t(`board.${g}`)}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 rounded-full border border-white/10 bg-white/5 p-1">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`board-period-${p}`}
            className={`h-10 rounded-full text-sm font-semibold transition-colors ${period === p ? "bg-amber-500 text-[#0B0D12]" : "text-slate-400"}`}>{t(`board.${p}`)}</button>
        ))}
      </div>
      <section className="surface p-4">
        {data?.length ? (
          <ol className="divide-y divide-white/5">
            {data.map((r) => (
              <li key={r.rank} className={`flex items-center gap-3 py-3 ${user && r.pseudo === user.pseudo ? "text-amber-300" : ""}`} data-testid={`board-row-${r.rank}`}>
                <span className={`grid h-9 w-9 place-items-center rounded-full font-display font-bold ${r.rank <= 3 ? "bg-amber-500 text-[#0B0D12]" : "bg-white/5 text-slate-400"}`}>
                  {r.rank === 1 ? <Crown className="h-4 w-4" /> : r.rank}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{r.pseudo}</span>
                <span className="text-xs text-slate-500">{t("board.wins", { w: r.wins, g: r.games })}{r.draws ? ` · ${t("board.draws", { d: r.draws })}` : ""}</span>
                <span className={`w-24 text-right font-mono text-sm font-semibold ${r.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatXAF(r.net, lang)}</span>
              </li>
            ))}
          </ol>
        ) : <p className="py-8 text-center text-sm text-slate-500" data-testid="board-empty">{t("board.empty")}</p>}
      </section>
      <p className="text-center text-xs text-slate-600">{t("board.note")}</p>
    </div>
  );
}
